import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { authFetch } from '../lib/authFetch';
import UpgradeModal from '../components/UpgradeModal';
import { isStudioTier } from '../lib/pricing';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FORMATS = [
  {
    id: 'short',
    name: 'Short Film',
    icon: '🎬',
    description: '5–30 min. One moment, one truth.',
  },
  {
    id: 'feature',
    name: 'Feature Film',
    icon: '🎞️',
    description: '90–120 min. Full arc, full world.',
  },
  {
    id: 'tv_series',
    name: 'TV Series',
    icon: '📺',
    description: 'Series bible, season arcs, episode beat sheets — pilot to finale.',
  },
];

export default function NewProject() {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [format, setFormat] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  const [upgradeModal, setUpgradeModal] = useState(false);

  useEffect(() => {
    authFetch(`${API}/auth/me`, { credentials: 'include' })
      .then(r => r.json())
      .then(setUser)
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!title.trim()) {
      setError('Give your story a name.');
      return;
    }
    if (!format) {
      setError('Choose a format to continue.');
      return;
    }
    // Series Builder is Studio-only on the server; show the upgrade path
    // instead of letting the request 403.
    if (format === 'tv_series' && user && !isStudioTier(user.subscription_tier)) {
      setUpgradeModal(true);
      return;
    }

    setLoading(true);
    try {
      const res = await authFetch(`${API}/projects/plot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ title: title.trim(), format }),
      });

      const data = await res.json();

      if (res.status === 403) {
        toast.error(data.detail || 'Project limit reached. Upgrade to create more.');
        navigate('/upgrade');
        return;
      }
      if (!res.ok) throw new Error(data.detail || 'Failed to create project');

      if (format === 'tv_series') {
        navigate(`/series/${data.project_id}`);
      } else {
        navigate(`/project/${data.project_id}`);
      }
    } catch (err) {
      setError(err.message || 'Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {upgradeModal && (
        <UpgradeModal
          title="Series Builder is a Studio feature."
          description="Series bible, season arcs and episode beat sheets — pilot to finale — are part of the Studio plan (KES 1,299/mo), or Founding for life."
          benefits={['Series Builder', 'Unlimited projects', 'Unlimited AI Mentor', 'Modular exports']}
          ctaText="Upgrade to Studio"
          onClose={() => setUpgradeModal(false)}
        />
      )}
      <style>{`
        .np-input {
          width: 100%;
          background: #111;
          border: 1px solid rgba(255,255,255,0.1);
          color: #e5e5e5;
          font-family: 'Courier Prime', monospace;
          font-size: 1rem;
          padding: 14px 16px;
          outline: none;
          transition: border-color 0.18s;
          box-sizing: border-box;
        }
        .np-input:focus { border-color: rgba(232,160,32,0.6); }
        .np-input::placeholder { color: #3a3a3a; font-style: italic; }
        .np-card {
          background: #0f0f0f;
          border: 1px solid rgba(255,255,255,0.07);
          padding: 20px 16px;
          cursor: pointer;
          transition: border-color 0.18s, background 0.18s;
          text-align: left;
        }
        .np-card:hover { border-color: rgba(232,160,32,0.4); background: #141414; }
        .np-card.selected {
          border-color: #E8A020;
          background: rgba(232,160,32,0.06);
          box-shadow: 0 0 20px rgba(232,160,32,0.12);
        }
        .np-btn {
          width: 100%;
          background: #E8A020;
          color: #000;
          font-family: 'Courier Prime', monospace;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.16em;
          font-size: 0.78rem;
          padding: 16px;
          border: none;
          cursor: pointer;
          transition: background 0.18s, box-shadow 0.18s, opacity 0.18s;
          box-shadow: 0 0 28px rgba(232,160,32,0.35);
        }
        .np-btn:hover:not(:disabled) { background: #f0b030; box-shadow: 0 0 48px rgba(232,160,32,0.6); }
        .np-btn:disabled { opacity: 0.4; cursor: not-allowed; box-shadow: none; }
        .np-skip {
          background: none;
          border: none;
          color: #404040;
          font-family: 'Courier Prime', monospace;
          font-size: 0.68rem;
          text-transform: uppercase;
          letter-spacing: 0.14em;
          cursor: pointer;
          transition: color 0.18s;
          padding: 0;
        }
        .np-skip:hover { color: #707070; }
      `}</style>

      <div style={{
        minHeight: '100vh',
        background: '#0A0A0A',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px 24px',
      }}>
        {/* Ambient glow */}
        <div style={{
          position: 'fixed', inset: 0,
          background: 'radial-gradient(ellipse 70% 50% at 50% 30%, rgba(232,160,32,0.07) 0%, transparent 65%)',
          pointerEvents: 'none', zIndex: 0,
        }} />

        <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: '560px' }}>
          {/* Logo */}
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <div style={{
              fontFamily: 'Courier Prime, monospace',
              fontWeight: 700,
              fontSize: '1.3rem',
              color: '#E8A020',
              letterSpacing: '0.16em',
              marginBottom: '6px',
            }}>
              PANGAJI
            </div>
            <p style={{
              fontFamily: 'Courier Prime, monospace',
              fontSize: '0.6rem',
              textTransform: 'uppercase',
              letterSpacing: '0.3em',
              color: '#505050',
            }}>
              The Story Engine
            </p>
          </div>

          {/* Headline */}
          <div style={{ marginBottom: '40px' }}>
            <h1 style={{
              fontFamily: 'Courier Prime, monospace',
              fontWeight: 700,
              fontSize: 'clamp(1.6rem, 4vw, 2.2rem)',
              color: '#fff',
              margin: '0 0 10px',
              lineHeight: 1.2,
            }}>
              Let's build your story.
            </h1>
            <p style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.9rem',
              color: '#505050',
              margin: 0,
            }}>
              Name your project and pick a format to get started.
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Title input */}
            <div style={{ marginBottom: '32px' }}>
              <label style={{
                display: 'block',
                fontFamily: 'Courier Prime, monospace',
                fontSize: '0.6rem',
                textTransform: 'uppercase',
                letterSpacing: '0.22em',
                color: '#606060',
                marginBottom: '8px',
              }}>
                Project Title
              </label>
              <input
                className="np-input"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. The Weight of Rain"
                autoFocus
                data-testid="new-project-title"
              />
            </div>

            {/* Format cards */}
            <div style={{ marginBottom: '32px' }}>
              <label style={{
                display: 'block',
                fontFamily: 'Courier Prime, monospace',
                fontSize: '0.6rem',
                textTransform: 'uppercase',
                letterSpacing: '0.22em',
                color: '#606060',
                marginBottom: '12px',
              }}>
                Format
              </label>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '10px',
              }}>
                {FORMATS.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    className={`np-card${format === f.id ? ' selected' : ''}`}
                    onClick={() => setFormat(f.id)}
                    data-testid={`format-card-${f.id}`}
                  >
                    <div style={{ fontSize: '1.4rem', marginBottom: '8px' }}>{f.icon}</div>
                    <div style={{
                      fontFamily: 'Courier Prime, monospace',
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      color: format === f.id ? '#E8A020' : '#d0d0d0',
                      marginBottom: '4px',
                      transition: 'color 0.18s',
                    }}>
                      {f.name}
                    </div>
                    <div style={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '0.72rem',
                      color: '#505050',
                      lineHeight: 1.4,
                    }}>
                      {f.description}
                    </div>
                    {format === f.id && (
                      <div style={{
                        marginTop: '10px',
                        width: '20px',
                        height: '2px',
                        background: '#E8A020',
                      }} />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Error */}
            {error && (
              <p style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.8rem',
                color: '#f87171',
                marginBottom: '16px',
              }}>
                {error}
              </p>
            )}

            {/* CTA */}
            <button
              type="submit"
              className="np-btn"
              disabled={loading || !title.trim() || !format}
              data-testid="new-project-submit"
            >
              {loading ? 'Creating...' : 'Create Project →'}
            </button>
          </form>

          {/* Skip */}
          <div style={{ textAlign: 'center', marginTop: '28px' }}>
            <button
              className="np-skip"
              onClick={() => navigate('/dashboard')}
              data-testid="new-project-skip"
            >
              Skip — go to dashboard
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
